/**
 * Protocolo da partida online (item 6.1): o servidor manda "fotos" compactas do mundo e o cliente monta de volta
 * um `WorldState` para desenhar. Posições e velocidades vão em centímetros (inteiros), a direção da vassoura em
 * décimos de milésimo; o que não muda na partida (time, posição, atributos, nome) vai uma vez, na escalação.
 */
import type { FlightStats, Position } from "@besenliga/shared";
import type { BallsState, FoulKind, PetardoMode, PlayEvent, PlayKind } from "./balls.js";
import type { AthleteState, Team } from "./flight.js";
import type { EndReason, MatchState, Phase } from "./match.js";
import type { WorldState } from "./world.js";

/** Quem está em campo: o que não muda durante a partida. */
export interface RosterEntry {
  id: string;
  team: Team;
  role: Position;
  name: string;
  /** Controlado por uma pessoa (senão, por bot). */
  human: boolean;
  stats: FlightStats;
  /** Aparência escolhida no criador (o cliente sabe ler; o servidor só repassa). */
  look?: unknown;
}

/** Mensagens do servidor para o cliente. */
export const MSG = {
  /** Servidor → cliente: seu atleta e a escalação. */
  welcome: "boas-vindas",
  /** Servidor → todos: a escalação mudou (alguém entrou ou saiu). */
  roster: "escalacao",
  /** Servidor → todos: foto do mundo. */
  snapshot: "foto",
  /** Servidor → todos: números da partida no fim (para a tela de resultado). */
  result: "resultado",
  /** Cliente → servidor: comando do tick. */
  command: "comando",
} as const;

export interface Welcome {
  you: string;
  roster: RosterEntry[];
  tickHz: number;
}

/** Foto: o tick e um vetor de inteiros, mais as jogadas desde a foto anterior. */
export interface Snapshot {
  tick: number;
  data: number[];
  plays: number[][];
  /** Por vaga da escalação: número do último comando da pessoa já aplicado (0 para bots). Item 6.2. */
  acks?: number[];
}

const POS = 100;
const DIR = 10000;
const STA = 1000;
const q = (v: number, k: number) => Math.round(v * k);

const PHASES: Phase[] = ["inicio", "jogo", "gol", "penalti", "fim"];
const REASONS: (EndReason | null)[] = [null, "faisca", "tempo", "morte-subita", "empate"];
const TEAMS: (Team | null)[] = [null, "casa", "fora"];
const WINNERS: (Team | "empate" | null)[] = [null, "casa", "fora", "empate"];
const MODES: PetardoMode[] = ["descanso", "caca", "lancado"];
const KINDS: PlayKind[] = [
  "posse",
  "passe",
  "arremesso",
  "gol",
  "solta",
  "roubo",
  "defesa",
  "rebatida",
  "atordoado",
  "aparece",
  "captura",
  "inicio",
  "falta",
  "penalti",
  "reinicio",
  "morte-subita",
  "fim",
  "habilidade",
  "queda",
];
const FOULS: (FoulKind | null)[] = [null, "empurrao", "bloqueio", "petardo-goleiro"];

const ATHLETE_FIELDS = 15;

/** Codifica o mundo na ordem da escalação. */
export function encodeSnapshot(
  w: Readonly<WorldState>,
  roster: readonly RosterEntry[],
  plays: readonly PlayEvent[],
  acks?: readonly number[],
): Snapshot {
  const index = new Map(roster.map((r, i) => [r.id, i]));
  const d: number[] = [];
  for (const r of roster) {
    const a = w.athletes.find((x) => x.id === r.id);
    if (!a) {
      for (let i = 0; i < ATHLETE_FIELDS; i++) d.push(0);
      continue;
    }
    d.push(
      q(a.position.x, POS),
      q(a.position.y, POS),
      q(a.position.z, POS),
      q(a.velocity.x, POS),
      q(a.velocity.y, POS),
      q(a.velocity.z, POS),
      q(a.heading.x, DIR),
      q(a.heading.z, DIR),
      q(a.stamina, STA),
      (a.turbo ? 1 : 0) | (a.exhausted ? 2 : 0),
      a.stunTicks,
      a.chargeTicks,
      a.abilityTicks,
      a.abilityCooldown,
      a.buttons,
    );
  }
  const b = w.balls;
  if (b) {
    const p = b.pelota;
    d.push(q(p.position.x, POS), q(p.position.y, POS), q(p.position.z, POS));
    d.push(q(p.velocity.x, POS), q(p.velocity.y, POS), q(p.velocity.z, POS));
    d.push(p.holder ? (index.get(p.holder) ?? -1) : -1);
    for (const pt of b.petardos) {
      d.push(q(pt.position.x, POS), q(pt.position.y, POS), q(pt.position.z, POS));
      d.push(q(pt.velocity.x, POS), q(pt.velocity.y, POS), q(pt.velocity.z, POS));
      d.push(MODES.indexOf(pt.mode));
    }
    const f = b.faisca;
    d.push(f.active ? 1 : 0, q(f.position.x, POS), q(f.position.y, POS), q(f.position.z, POS));
    d.push(q(f.velocity.x, POS), q(f.velocity.y, POS), q(f.velocity.z, POS));
  }
  const m = w.match;
  if (m) {
    d.push(
      PHASES.indexOf(m.phase),
      m.phaseTicks,
      m.clockTicks,
      m.suddenDeath ? 1 : 0,
      m.suddenDeathTicks,
      m.score.casa,
      m.score.fora,
      m.goals.casa,
      m.goals.fora,
      WINNERS.indexOf(m.winner),
      REASONS.indexOf(m.endReason),
      TEAMS.indexOf(m.penalty?.team ?? null),
      m.fouls.casa,
      m.fouls.fora,
    );
  }
  return {
    tick: w.tick,
    ...(acks ? { acks: [...acks] } : {}),
    data: d,
    plays: plays.map((p) => [
      KINDS.indexOf(p.kind),
      index.get(p.athlete) ?? -1,
      p.other ? (index.get(p.other) ?? -1) : -1,
      TEAMS.indexOf(p.team ?? null),
      FOULS.indexOf(p.foul ?? null),
      p.hoop ?? -1,
    ]),
  };
}

const v = (x: number | undefined, y: number | undefined, z: number | undefined) => ({
  x: (x ?? 0) / POS,
  y: (y ?? 0) / POS,
  z: (z ?? 0) / POS,
});

/**
 * Monta de volta um `WorldState` para desenhar. O que a foto não traz (sorteio, estatísticas internas, botões
 * guardados das bolas) fica com valores neutros: o cliente não simula a partida, só mostra.
 */
export function decodeSnapshot(s: Snapshot, roster: readonly RosterEntry[]): WorldState {
  const d = s.data;
  let i = 0;
  const next = () => d[i++] ?? 0;
  const athletes: AthleteState[] = roster.map((r) => {
    const position = v(next(), next(), next());
    const velocity = v(next(), next(), next());
    const heading = { x: next() / DIR, z: next() / DIR };
    const stamina = next() / STA;
    const flags = next();
    return {
      id: r.id,
      team: r.team,
      role: r.role,
      position,
      velocity,
      heading,
      stamina,
      turbo: (flags & 1) !== 0,
      exhausted: (flags & 2) !== 0,
      stunTicks: next(),
      chargeTicks: next(),
      abilityTicks: next(),
      abilityCooldown: next(),
      buttons: next(),
      stats: r.stats,
    };
  });
  const ppos = v(next(), next(), next());
  const pvel = v(next(), next(), next());
  const holderIdx = next();
  const petardos = [0, 1].map(() => {
    const position = v(next(), next(), next());
    const velocity = v(next(), next(), next());
    return {
      position,
      velocity,
      mode: MODES[next()] ?? "descanso",
      modeTicks: 0,
      hitBy: null,
      ignore: null,
      ignoreTicks: 0,
    };
  });
  const factive = next() === 1;
  const fpos = v(next(), next(), next());
  const fvel = v(next(), next(), next());
  const balls: BallsState = {
    pelota: {
      position: ppos,
      velocity: pvel,
      holder: holderIdx >= 0 ? (roster[holderIdx]?.id ?? null) : null,
      lastTouch: null,
      lastTeam: null,
      regrabTicks: 0,
    },
    petardos,
    faisca: {
      active: factive,
      captured: false,
      capturedBy: null,
      position: fpos,
      velocity: fvel,
      waypoint: fpos,
      wanderTicks: 0,
      activeTicks: 0,
      appearTick: 0,
    },
  };
  let match: MatchState | null = null;
  if (i < d.length) {
    const phase = PHASES[next()] ?? "jogo";
    const phaseTicks = next();
    const clockTicks = next();
    const suddenDeath = next() === 1;
    const suddenDeathTicks = next();
    const score = { casa: next(), fora: next() };
    const goals = { casa: next(), fora: next() };
    const winner = WINNERS[next()] ?? null;
    const endReason = REASONS[next()] ?? null;
    const penaltyTeam = TEAMS[next()] ?? null;
    const fouls = { casa: next(), fora: next() };
    match = {
      phase,
      phaseTicks,
      clockTicks,
      suddenDeath,
      suddenDeathTicks,
      score,
      goals,
      fouls,
      winner,
      endReason,
      penalty: penaltyTeam ? { taker: "", keeper: null, team: penaltyTeam, shotTicks: null } : null,
      blockTicks: {},
      restartTeam: null,
      stats: {},
      pendingPass: null,
    };
  }
  const id = (k: number) => (k >= 0 ? (roster[k]?.id ?? "") : "");
  const plays: PlayEvent[] = s.plays.map((p) => {
    const e: PlayEvent = { kind: KINDS[p[0] ?? 0] ?? "posse", athlete: id(p[1] ?? -1) };
    const other = p[2] ?? -1;
    if (other >= 0) e.other = id(other);
    const team = TEAMS[p[3] ?? 0];
    if (team) e.team = team;
    const foul = FOULS[p[4] ?? 0];
    if (foul) e.foul = foul;
    if ((p[5] ?? -1) >= 0) e.hoop = p[5] ?? 0;
    return e;
  });
  return { tick: s.tick, rng: 0, athletes, events: [], balls, plays, match };
}
